// src/context/AuthContext.tsx

import {
  createContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from 'react'
import type { TokenResponse, User } from '../types/auth'
import * as authService from '../services/authService'
import * as userProfileService from '../services/userProfileService'

interface FullProfile extends User {
  id?: string
  createdAt?: string
  lastLogin?: string
}

interface AuthContextValue {
  user: User | null
  fullProfile: FullProfile | null
  token: string | null
  initialized: boolean
  loading: boolean
  error: string | null
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  fetchFullProfile: () => Promise<void>
  updateProfile: (updates: Record<string, any>) => Promise<void>
  deleteAccount: () => Promise<void>
}

export const AuthContext = createContext<AuthContextValue | null>(null)

// réexport du type pour que le hook puisse l'utiliser
export type { AuthContextValue }

const ACCESS_KEY = 'accessToken'
const REFRESH_KEY = 'refreshToken'

function parseJwt(token: string): Partial<User> | null {
  try {
    const base64 = token.split('.')[1]
    const json = atob(base64)
    const payload: Record<string, any> = JSON.parse(json)

    // attempt to extract email from common claim names
    let email = payload?.email || ''
    if (!email) {
      // search for any key containing "email" case-insensitive
      for (const k of Object.keys(payload)) {
        if (/email/i.test(k) && typeof payload[k] === 'string') {
          email = payload[k]
          break
        }
      }
    }

    // attempt to extract roles from common claim names
    let roles: string[] = payload?.roles || payload?.role || []
    if (!Array.isArray(roles)) {
      roles = [roles].filter(Boolean)
    }
    if (roles.length === 0) {
      // search for any key containing "role" case-insensitive
      for (const k of Object.keys(payload)) {
        if (/role/i.test(k)) {
          const value = payload[k]
          if (Array.isArray(value)) {
            roles = value
          } else if (typeof value === 'string') {
            roles = [value]
          }
          break
        }
      }
    }

    // extract user id from nameidentifier
    let userId = payload?.sub || ''
    if (!userId) {
      for (const k of Object.keys(payload)) {
        if (/nameidentifier/i.test(k) && typeof payload[k] === 'string') {
          userId = payload[k]
          break
        }
      }
    }

    return {
      username: payload?.unique_name || payload?.name || payload?.sub || '',
      email,
      roles,
      id: userId,
    }
  } catch {
    return null
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(null)
  const [user, setUser] = useState<User | null>(null)
  const [fullProfile, setFullProfile] = useState<FullProfile | null>(null)
  const [initialized, setInitialized] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const syncUserFromToken = useCallback(
    (t: string | null) => {
      if (t) {
        const info = parseJwt(t)
        if (info && info.email) {
          setUser({
            username: info.username || '',
            email: info.email,
            roles: info.roles as string[] | undefined,
            id: info.id,
          })
        } else {
          setUser(null)
        }
      } else {
        setUser(null)
      }
    },
    [setUser]
  )

  useEffect(() => {
    const saved = localStorage.getItem(ACCESS_KEY)
    if (saved) {
      setToken(saved)
      syncUserFromToken(saved)
    }
    // quel que soit le cas, on signale que l'initialisation est terminée
    setInitialized(true)
  }, [syncUserFromToken])

  const saveTokens = (tokens: TokenResponse) => {
    localStorage.setItem(ACCESS_KEY, tokens.accessToken)
    localStorage.setItem(REFRESH_KEY, tokens.refreshToken)
    setToken(tokens.accessToken)
    syncUserFromToken(tokens.accessToken)
  }

  const login = async (email: string, password: string) => {
    setLoading(true)
    setError(null)
    try {
      const tokens = await authService.login({ email, password })
      saveTokens(tokens)
      // Vérifier que l'utilisateur a le rôle admin
      const currentUser = parseJwt(tokens.accessToken)
      if (!currentUser?.roles?.includes('Admin')) {
        logout()
        throw new Error('Accès refusé. Seuls les administrateurs peuvent se connecter.')
      }
      // charger immédiatement le profil complet
      await fetchFullProfile().catch(() => null)
    } catch (err: any) {
      setError(err.message || 'Erreur')
      throw err
    } finally {
      setLoading(false)
    }
  }

  const logout = () => {
    setToken(null)
    setUser(null)
    setFullProfile(null)
    localStorage.removeItem(ACCESS_KEY)
    localStorage.removeItem(REFRESH_KEY)
  }

  const fetchFullProfile = async () => {
    if (!token) throw new Error('Pas de token')
    setLoading(true)
    setError(null)
    try {
      const profile = await userProfileService.fetchUserProfile(token)
      setFullProfile(profile)
    } catch (err: any) {
      setError(err.message || 'Erreur')
      throw err
    } finally {
      setLoading(false)
    }
  }

  const updateProfile = async (updates: Record<string, any>) => {
    if (!token) throw new Error('Pas de token')
    setLoading(true)
    setError(null)
    try {
      const updated = await userProfileService.updateUserProfile(token, updates)
      setFullProfile(updated)
      // Mettre à jour aussi l'info basique du token
      setUser((prev) => ({
        ...(prev || {}),
        ...updates,
      } as User))
    } catch (err: any) {
      setError(err.message || 'Erreur')
      throw err
    } finally {
      setLoading(false)
    }
  }

  const deleteAccount = async () => {
    if (!token) throw new Error('Pas de token')
    setLoading(true)
    setError(null)
    try {
      await userProfileService.deleteUserProfile(token)
      logout()
    } catch (err: any) {
      setError(err.message || 'Erreur')
      throw err
    } finally {
      setLoading(false)
    }
  }

  const value: AuthContextValue = {
    user,
    fullProfile,
    token,
    initialized,
    loading,
    error,
    login,
    logout,
    fetchFullProfile,
    updateProfile,
    deleteAccount,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
